/**
 * @fileoverview Evidence Packet Gate Hook
 * @module useEvidencePacketGate
 * 
 * Business Context:
 * - Manages the gate between Step 2 (File Appeal) and Step 3 (Find Flaws)
 * - Only shows when user has filed their appeal (Step 2 complete)
 * - Checks if user has received evidence packet or skipped the dialog
 * - Uses sessionStorage for temporary skip during session
 * - Uses AppealOutcome data for persistent state
 * 
 * Technical Context:
 * - Combines sessionStorage (temporary) with API data (persistent)
 * - Returns shouldShowDialog to determine if gate should appear
 */

import { useState, useEffect, useCallback } from 'react';

const SESSION_SKIP_KEY = 'tbyt_evidence_packet_skipped';

interface AppealOutcome {
  id: string;
  evidencePacketReceived: boolean | null;
  evidencePacketSkipped: boolean;
  hearingDate: string | null;
}

interface UseEvidencePacketGateProps {
  appealOutcome: AppealOutcome | null;
  step1Complete: boolean; // Only show gate if Step 2 (File Appeal) is complete - named step1Complete because it tracks module-01
}

interface UseEvidencePacketGateReturn {
  /** Whether the gate dialog should be shown */
  shouldShowDialog: boolean;
  /** Show the dialog */
  showDialog: () => void;
  /** Hide the dialog */
  hideDialog: () => void;
  /** Mark as skipped for this session */
  markSessionSkipped: () => void;
  /** Whether dialog is currently visible */
  isDialogOpen: boolean;
  /** Whether user has provided evidence packet info */
  hasProvidedInfo: boolean;
  /** Whether user can enter meeting date later (skipped but not completed) */
  canEnterLater: boolean;
}

export function useEvidencePacketGate({
  appealOutcome,
  step1Complete
}: UseEvidencePacketGateProps): UseEvidencePacketGateReturn {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sessionSkipped, setSessionSkipped] = useState(false);

  // Check sessionStorage on mount
  useEffect(() => {
    const skipped = sessionStorage.getItem(SESSION_SKIP_KEY) === 'true';
    setSessionSkipped(skipped);
  }, []);

  // Determine if user has already provided evidence packet info
  const hasProvidedInfo = Boolean(
    appealOutcome?.evidencePacketReceived !== null && 
    appealOutcome?.evidencePacketReceived !== undefined
  );

  // User has skipped (either in DB or session)
  const hasSkipped = Boolean(
    appealOutcome?.evidencePacketSkipped || sessionSkipped
  );

  // User can enter later if they skipped but haven't provided info yet
  const canEnterLater = hasSkipped && !hasProvidedInfo;

  // Should show dialog if:
  // 1. Step 2 (File Appeal) is complete (appeal filed) - tracked via step1Complete/module-01
  // 2. User hasn't provided evidence packet info
  // 3. User hasn't skipped (either in DB or session)
  const shouldShowDialog = Boolean(
    step1Complete &&
    !hasProvidedInfo &&
    !hasSkipped
  );

  const showDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const hideDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const markSessionSkipped = useCallback(() => {
    sessionStorage.setItem(SESSION_SKIP_KEY, 'true');
    setSessionSkipped(true);
    setIsDialogOpen(false);
  }, []);

  return {
    shouldShowDialog,
    showDialog,
    hideDialog,
    markSessionSkipped,
    isDialogOpen,
    hasProvidedInfo,
    canEnterLater
  };
}

export default useEvidencePacketGate;




